// Server-safe school-year derivation. Deliberately OUTSIDE the "use client"
// NaplanMarkContext module: the mark/practice pages derive the selector default
// server-side, and importing a value from a client module in a server component
// yields a client-reference proxy — the function silently reads as undefined
// (observed live: the profile-derived year never rendered). Unit tests cannot
// catch that, so the boundary is enforced by file placement.
import { NAPLAN_YEARS, type NaplanReportYear } from "./expectations";

// Two clock rules, both load-bearing for a marking product:
//  - Use SYDNEY time, not UTC: getUTCFullYear() is a year behind until ~11am
//    Sydney time on 1 January, which flipped the derived year mid-morning.
//  - January belongs to the PREVIOUS school year: the Australian school year
//    starts late January, and summer holidays are peak parent-marking season —
//    a child who finished Year 3 in December must not derive as Year 4+ on
//    2 January.
export const australianSchoolYearOf = (now: Date): number => {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "numeric",
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  return month === 1 ? year - 1 : year;
};

// Estimate the NAPLAN reporting year from a birth year. A Year N cohort spans
// calendar-year ages N+5 and N+6 (split by state intake cutoffs that a bare
// birth year cannot resolve), so the student's school year is age−5 OR age−6.
// We snap the midpoint (age − 5.5) to the nearest NAPLAN year, which bounds the
// error at ~1 year and, on the genuinely ambiguous ages, lands LOW — for a
// marking product, "Strong for Year 3" (checkable against the child's real
// NAPLAN report) beats "Developing for Year 5" (benchmarked against a cohort
// they may not have joined). Only school-age birth years derive at all; adults
// (e.g. a parent marking on their own account) and toddlers return null so the
// form asks instead of asserting.
export const naplanYearFromBirthYear = (
  birthYear: number | null,
  now: Date = new Date(),
): NaplanReportYear | null => {
  if (birthYear == null) return null;
  const age = australianSchoolYearOf(now) - birthYear;
  if (age < 6 || age > 18) return null;
  const estimate = age - 5.5;
  let best: NaplanReportYear = NAPLAN_YEARS[0];
  for (const y of NAPLAN_YEARS) {
    if (Math.abs(y - estimate) < Math.abs(best - estimate)) best = y;
  }
  return best;
};

// ---------------------------------------------------------------------------
// The year-level override pref (user_profiles.prefs.naplanMarkYear)
// ---------------------------------------------------------------------------
// A student's explicit correction of the derived year. Stored on the PROFILE —
// a durable fact that follows the learner across devices — and stamped with the
// school year it was set in: a correction means "your guess is wrong THIS
// year", and a child's year level advances every January, so an unstamped
// absolute year would go stale and silently mark them low forever.
export const NAPLAN_YEAR_PREF_KEY = "naplanMarkYear";

export interface NaplanYearPref {
  year: NaplanReportYear;
  setIn: number; // australianSchoolYearOf at the time of the correction
}

export const buildNaplanYearPref = (
  year: NaplanReportYear,
  now: Date = new Date(),
): NaplanYearPref => ({ year, setIn: australianSchoolYearOf(now) });

/** The remembered override, or null when absent, malformed, or stale. */
export const readNaplanYearPref = (
  prefs: Record<string, unknown> | undefined,
  now: Date = new Date(),
): NaplanReportYear | null => {
  const raw = prefs?.[NAPLAN_YEAR_PREF_KEY] as Partial<NaplanYearPref> | undefined;
  if (!raw || !(NAPLAN_YEARS as readonly number[]).includes(raw.year as number)) return null;
  if (raw.setIn !== australianSchoolYearOf(now)) return null; // expired — a new school year
  return raw.year as NaplanReportYear;
};
